import React, { memo, useRef, useEffect } from "react";
import { WEEKLY_CHECKS_BOARD_ID, METER_READINGS_BOARD_ID, TASKS_BOARD_ID } from "../../constants/columnIds";

const TaskItem = memo(function TaskItem({
  task,
  propertyName,
  handleTaskClick,
  handleToggle,
  isWeekly = false,
}) {
  const taskTextRef = useRef(null);
  const uniqueIdRef = useRef(`task-hover-${task.id}-${Math.random().toString(36).substr(2, 9)}`);
  
  // Use same styling for completed tasks (task.done) as completed entries
  const isCompleted = task.isCompletedEntry || task.done;
  const backgroundColor = isCompleted 
    ? `${task.boardColor || "#323338"}30`
    : `${task.boardColor || "#323338"}90`;
  
  // Check if this is a Weekly Checks task with subitems
  const hasSubitems = task.boardId === WEEKLY_CHECKS_BOARD_ID && 
                      task.subitems && 
                      task.subitems.length > 0;
  
  // Check if this is a Meter Readings task with notes
  const hasNotes = task.boardId === METER_READINGS_BOARD_ID && 
                   task.notes && 
                   task.notes.trim() !== "";
  
  // Allow clicking completed Meter Readings and TASKS tasks
  const isClickable = !task.isCompletedEntry || 
                      (task.isCompletedEntry && (task.boardId === METER_READINGS_BOARD_ID || task.boardId === TASKS_BOARD_ID)) ||
                      (task.done && (task.boardId === METER_READINGS_BOARD_ID || task.boardId === TASKS_BOARD_ID));

  // Inject CSS for hover effect using native CSS :hover
  useEffect(() => {
    if (!isClickable || (task.done && !(task.isCompletedEntry && (task.boardId === METER_READINGS_BOARD_ID || task.boardId === TASKS_BOARD_ID)) && !(task.done && (task.boardId === METER_READINGS_BOARD_ID || task.boardId === TASKS_BOARD_ID)))) {
      return;
    }

    const styleId = `task-hover-style-${uniqueIdRef.current}`;
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      .${uniqueIdRef.current}:hover {
        background-color: rgba(255, 255, 255, 0.5) !important;
      }
    `;

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [isClickable, task.done, task.isCompletedEntry, task.boardId]);

  return (
    <li
      style={{
        display: "flex",
        alignItems: "center",
        padding: "4px 0",
        fontSize: isWeekly ? 13 : undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          backgroundColor,
          borderRadius: 4,
          padding: "6px 8px",
          opacity: isCompleted ? 0.6 : 1,
        }}
      >
        <input
          type="checkbox"
          checked={task.isCompletedEntry ? true : task.done}
          onChange={(e) => {
            if (!task.isCompletedEntry) {
              e.stopPropagation();
              handleToggle({ propertyName, taskId: task.id });
            }
          }}
          disabled={task.isCompletedEntry}
          style={{ 
            marginRight: isWeekly ? 6 : 8, 
            cursor: task.isCompletedEntry ? "default" : "pointer" 
          }}
        />
        {/* Show clock icon for tasks with time tracking enabled */}
        {task.boardId === TASKS_BOARD_ID && task.timeTracking && String(task.timeTracking).toLowerCase().trim() === "yes" && (
          <svg
            width={isWeekly ? 12 : 14}
            height={isWeekly ? 12 : 14}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              marginRight: 6,
              display: "inline-flex",
              alignItems: "center",
              color: isCompleted ? "#676879" : "#323338",
              flexShrink: 0,
            }}
            title="Time tracking enabled"
          >
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M8 4v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
          </svg>
        )}
        <span
          ref={taskTextRef}
          onClick={isClickable ? () => handleTaskClick(task, propertyName) : undefined}
          className={isClickable && (!task.done || (task.isCompletedEntry && task.boardId === METER_READINGS_BOARD_ID)) 
            ? uniqueIdRef.current 
            : undefined}
          style={{
            textDecoration: "none", // Remove line-through for completed tasks
            color: isCompleted ? "#676879" : "#323338",
            cursor: isClickable ? "pointer" : "default",
            flex: 1,
            padding: "2px 4px",
            borderRadius: 4,
            transition: "background-color 0.2s",
            fontStyle: isCompleted ? "italic" : "normal",
            backgroundColor: "transparent",
          }}
          title={
            hasSubitems 
              ? `${task.subitems.length} extra task${task.subitems.length > 1 ? 's' : ''}`
              : hasNotes 
                ? "Has notes"
                : undefined
          }
        >
          {task.name}
          {(hasSubitems || hasNotes) && (
            <span style={{ 
              marginLeft: 4, 
              color: "#d83a52",
              fontWeight: 600,
              fontSize: "1.5em",
              lineHeight: "1",
              verticalAlign: "middle",
              display: "inline-flex",
              alignItems: "center"
            }}>
              +
            </span>
          )}
        </span>
      </div>
    </li>
  );
});

export default TaskItem;

